import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { EndUser } from '@shared/models/EndUser.model';
import { EmergencyContact } from '@shared/models/EmergencyContact.model';
import { CommandResponse, SendCommandPayload } from '@shared/models/command-models/CommandResponse';
import { CommandRecord } from '@shared/models/command-models/CommandRecord.model';
import { Caretaker } from '@shared/models/Caretaker.model';
import { UserWithWatch } from '@shared/models/UserWithWatch.model';
import { FunctionState } from '@shared/models/command-models/CommandParameters';
import { ResponseParameter, ResponseParameterPayload } from '@shared/models/command-models/ResponseParameter';
import { Watch } from '@shared/models/Watch.model';
import { UserAddress } from '@shared/models/UserAddress.model';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  constructor( private http: HttpClient ) { }

  public getCaretaker(): Observable<Caretaker> {
    return this.http.get<Caretaker>(`${environment.baseAPIUrl}caretaker`);
  }

  public updateCaretaker(caretaker: Partial<Caretaker>): Observable<Caretaker> {
    return this.http.put<Caretaker>(`${environment.baseAPIUrl}caretaker`, caretaker);
  }

  public getAllDevices(): Observable<UserWithWatch[]> {
    return this.http.get<UserWithWatch[]>(`${environment.baseAPIUrl}caretaker/getDeviceList`);
  }

  public updateWatchFields(payload: Watch): Observable<any> {
    return this.http.put(`${environment.baseAPIUrl}devices`, payload);
  }

  public updateEndUser(data: EndUser): Observable<EndUser> {
    return this.http.put<EndUser>(`${environment.baseAPIUrl}end-users/`, data);
  }

  public getEmergencyContacts(id: number): Observable<EmergencyContact[]> {
    return this.http.get<EmergencyContact[]>(`${environment.baseAPIUrl}end-user-emergency-contacts/${id}/`);
  }

  public updateEmergencyContact(data: EmergencyContact): Observable<EmergencyContact> {
    return this.http.put<EmergencyContact>(`${environment.baseAPIUrl}emergency-contacts/`, data);
  }

  public createEmergencyContact(data: EmergencyContact): Observable<EmergencyContact> {
    return this.http.post<EmergencyContact>(`${environment.baseAPIUrl}emergency-contacts/`, data);
  }

  public deleteEmergencyContact(id: number): Observable<any> {
    return this.http.delete(`${environment.baseAPIUrl}emergency-contacts/${id}`);
  }

  public sendCommand<T>(data: SendCommandPayload<T>): Observable<CommandResponse<T>> {
    return this.http.post<CommandResponse<T>>(`${environment.senderCommandUrl}sendCommand/`, data);
  }

  public getCommandByID(id: number): Observable<CommandRecord> { // maybe useless, watch out
    return this.http.get<CommandRecord>(`${environment.baseAPIUrl}commands/${id}`);
  }

  public getCommandsByDevice(id: number): Observable<FunctionState[]> {
    return this.http.get<FunctionState[]>(`${environment.senderCommandUrl}getCommandsByDeviceId/${id}`);
  }

  public getResponsesByCommandId(payload: { commandId: number, start: number, end: number}): Observable<any> { // maybe useless, watch out
    const { commandId, start, end } = payload;
    return this.http.get(`${environment.senderCommandUrl}getCommandResponsesByCommandIdStartXEndY/${commandId}?start=${start}&end=${end}`);
  }

  public getCommandResponses<T>(payload: ResponseParameterPayload): Observable<ResponseParameter<T>[]> {
    const { id, name, start, end } = payload;
    const params = new HttpParams()
      .set('commandName', name)
      .set('startDate', start)
      .set('endDate', end);
    return this.http.get<ResponseParameter<T>[]>(`${environment.senderCommandUrl}getCommandResponseParametersByDeviceIdAndCommandNameFromDateToDate/${id}`, {params});
  }

  public getLocation(userAddress: UserAddress) {
    return this.http.get(`https://nominatim.openstreetmap.org/search.php?street=${userAddress.street}&city=${userAddress.city}&country=${userAddress.country}&postalcode=9427&polygon_geojson=1&format=jsonv2`);
  }

  public getCountryLocation(country: string) {
    return this.http.get(`https://nominatim.openstreetmap.org/search.php?country=${country}&polygon_geojson=1&format=jsonv2`); 
  }

  
  public getHeartRateMonitor<T>(payload: ResponseParameterPayload): Observable<ResponseParameter<T>[]> {
    const { id, start, end } = payload;
    const params = new HttpParams()
      .set('startDate', start)
      .set('endDate', end);
    return this.http.get<ResponseParameter<T>[]>(`${environment.senderCommandUrl}getCommandResponseParametersByDeviceIdForHeartRateMonitor/${id}`, {params});
  }
}
